/**
 * Procedural rock/boulder scatter near the route and viewpoints (issue #63):
 * ground-level visual anchoring, not a walkable or LOS-relevant feature.
 *
 * Draw calls: exactly one InstancedMesh per preset (ROCK_PRESET_NAMES.length
 * === 4), all sharing ONE material — 4 draw calls total, at the issue's ≤4
 * budget. Unlike trees, this layer does NOT swap a rock's
 * representation by camera distance: rockGeometry.ts's generator supports
 * two LOD detail levels from one seed (NEAR_DETAIL/FAR_DETAIL, tested there
 * for a matching silhouette), but wiring a live near/far swap would need a
 * second bucket per preset (8 buckets total), busting the ≤4 draw-call
 * acceptance criterion. Given rocks are placed at a bounded tens-to-low-
 * hundreds count (a tiny fraction of trees' thousands), a single
 * fixed NEAR_DETAIL geometry per preset is cheap enough at every distance
 * that the perf case for runtime LOD swapping is far weaker here — a
 * deliberate scope cut, not an oversight.
 *
 * Boot cost / no build-time bake: unlike issue #58's trees (a real branching
 * grammar bake serialised to a static asset), the 4 deployed geometries here
 * are generated once at construction, at runtime. Each is a single ~642-
 * vertex/1280-triangle icosphere run through a handful of noise evaluations
 * per vertex, which is well inside the issue's ~50 ms boot budget — so this
 * skips the bake/serialize/load machinery entirely rather than adding it for
 * a generator that's already cheap enough to run live.
 *
 * Placement is bounded (tens-to-low-hundreds, rockPlacement.ts) and grounded
 * once via sampleGround under the standard per-tick sample budget; because
 * the total instance count is so small, unlike VegetationLayer
 * this layer does NOT reconcile activation by camera distance — every
 * instance, once grounded, keeps its InstancedMesh slot for the layer's
 * lifetime (no retain-radius/deactivate bookkeeping, which would be unused
 * complexity at this scale). Purely cosmetic scene furniture — NO bearing on
 * any LOS result (hard constraint #1, guarded by rocks.guard.test.ts); no
 * elevation is invented (hard constraint #6) — every rock grounds on the
 * same DTM collider that backs walk-mode ground lock, offset so its LOWEST
 * vertex (not its origin) sits exactly at the sampled ground height (the
 * "contact ring"), never floating or sinking.
 *
 * Shading: per-vertex strata tint + slope-keyed moss/lichen noise in one
 * shared UV-less material (onBeforeCompile). The moss/lichen patchiness uses
 * the same GLSL cell-hash idiom as this codebase's other seeded scatter
 * shaders (`fract(sin(dot(cell, vec2(12.9898,78.233)))*43758.5453)`) rather
 * than inventing a new noise scheme. The issue's suggestion to reuse
 * terrainNoise.ts's fbm helper didn't fit directly: that module bakes a 2D
 * UV-tileable noise texture for the terrain material, which isn't something a
 * fragment shader can inline; this module DOES reuse terrainNoise.ts's
 * `srgbToLinearRgb` for authoring its rock colours in sRGB.
 *
 * Attribution: displacement field, presets and per-vertex vdata channels
 * ported from Braffolk/fable5-world-demo `RockBuilder.ts` (MIT licence,
 * commit fd75fdb) — see outputs/qa/mining/05_visual_quality.md §7.
 */
import {
  BufferAttribute,
  BufferGeometry,
  DoubleSide,
  Group,
  InstancedMesh,
  Matrix4,
  MeshStandardMaterial,
  Quaternion,
  Vector3,
  type WebGLProgramParametersWithUniforms,
} from 'three';
import type { GroundSampler } from '../camera/groundLock';
import type { LocalXZ } from '../core/coordinates';
import { srgbToLinearRgb } from '../terrain/terrainNoise';
import { NEAR_DETAIL, ROCK_PRESET_NAMES, buildRockGeometry, type RockPreset } from './rockGeometry';
import { sampleRockInstances, type RockInstance } from './rockPlacement';
import type { TarmacClearance } from './tarmacClearance';

/** Fixed seed + detail every deployed preset bucket is built from (see file
 * header: no runtime LOD swap at this instance count). Exported so tests can
 * independently recompute a preset's `minY` for the contact-ring assertion. */
export const ROCK_DEPLOY_SEED = 1;
export const ROCK_DEPLOY_DETAIL = NEAR_DETAIL;

const Y_AXIS = new Vector3(0, 1, 0);
const ZERO_SCALE_MATRIX = new Matrix4().makeScale(0, 0, 0);

const ROCK_BASE_SRGB: [number, number, number] = [0.42, 0.4, 0.36];
const ROCK_BAND_SRGB: [number, number, number] = [0.55, 0.5, 0.42];
const MOSS_SRGB: [number, number, number] = [0.28, 0.36, 0.2];

export interface RocksLayerOptions {
  /** Route corridor (local x/z) rocks are planted along; may be empty when
   * only viewpoints should anchor rocks. */
  corridor: LocalXZ[];
  viewpoints?: LocalXZ[];
  /** Global cap on total rock instances (issue #63: tens to low hundreds). */
  instanceCap: number;
  /** InstancedMesh allocation capacity + number of instances placed, for a live
   * quality re-cap (issue #73): the active cap can be raised up to this without
   * reallocating a buffer or re-placing rocks. Defaults to instanceCap. main.ts
   * passes the antialias bucket's maximum. */
  maxInstanceCap?: number;
  corridorMaxDistM?: number;
  viewpointRadiusM?: number;
  densityScale?: number;
  /** Carriageway clearance (issue #79): drops rocks on the route ribbon or a
   * nearby tarmac road centreline. */
  clearance?: TarmacClearance;
}

interface Bucket {
  mesh: InstancedMesh;
  /** See BuiltRockGeometry.minY. */
  minY: number;
  /** Next never-used index; also the high-water mark → mesh.count. */
  nextFresh: number;
  /** Freed indices (below nextFresh) available for reuse after a live re-cap. */
  freed: number[];
  dirty: boolean;
}

interface InstanceRec extends RockInstance {
  resolvedY: number | null;
  attemptGen: number;
  /** Claimed render slot, or null when grounded-but-not-realised (issue #73:
   * the active cap may leave some grounded rocks unrealised). */
  slot: { preset: RockPreset; index: number } | null;
}

function glslVec3(rgb: [number, number, number]): string {
  return `vec3(${rgb[0].toFixed(5)}, ${rgb[1].toFixed(5)}, ${rgb[2].toFixed(5)})`;
}

export class RocksLayer {
  readonly group = new Group();
  private readonly material: MeshStandardMaterial;
  private readonly buckets = new Map<RockPreset, Bucket>();
  private readonly instances: InstanceRec[];
  /** Active cap (mutable for a live quality re-cap, issue #73). */
  private cap: number;
  /** InstancedMesh allocation capacity + placed-instance count (fixed). */
  private readonly allocCap: number;
  private liveCount = 0;
  private fallbackGeneration = 0;
  private readonly _pos = new Vector3();
  private readonly _quat = new Quaternion();
  private readonly _scl = new Vector3();
  private readonly _mat = new Matrix4();

  constructor(options: RocksLayerOptions) {
    this.group.name = 'rocks';
    this.cap = options.instanceCap;
    this.allocCap = Math.max(options.instanceCap, options.maxInstanceCap ?? options.instanceCap);

    this.material = new MeshStandardMaterial({ side: DoubleSide, roughness: 1, metalness: 0 });
    this.material.onBeforeCompile = (shader) => this.applyRockShader(shader);

    for (const preset of ROCK_PRESET_NAMES) {
      const built = buildRockGeometry(preset, ROCK_DEPLOY_SEED, ROCK_DEPLOY_DETAIL);
      const geometry = new BufferGeometry();
      geometry.setAttribute('position', new BufferAttribute(built.positions, 3));
      geometry.setAttribute('normal', new BufferAttribute(built.normals, 3));
      geometry.setAttribute('aVdata', new BufferAttribute(built.vdata, 3));
      geometry.setIndex(new BufferAttribute(built.indices, 1));
      const mesh = new InstancedMesh(geometry, this.material, this.allocCap);
      mesh.name = `rocks-${preset}`;
      mesh.count = 0; // nothing drawn until slots are claimed
      mesh.castShadow = true;
      this.group.add(mesh);
      this.buckets.set(preset, { mesh, minY: built.minY, nextFresh: 0, freed: [], dirty: false });
    }

    // Place up to the ALLOCATION cap so a live re-cap up to it needs no
    // re-placement (issue #73); the active cap bounds how many are realised.
    const placed = sampleRockInstances(options.corridor, {
      viewpoints: options.viewpoints,
      corridorMaxDistM: options.corridorMaxDistM,
      viewpointRadiusM: options.viewpointRadiusM,
      densityScale: options.densityScale,
      maxInstances: this.allocCap,
      clearance: options.clearance,
    });
    this.instances = placed.map((p) => ({ ...p, resolvedY: null, attemptGen: Number.NaN, slot: null }));
  }

  /** Live rock instances (bounded by the cap). */
  get liveInstanceCount(): number {
    return this.liveCount;
  }

  /** Draw calls currently submitted (meshes with count > 0) — ≤ 4. */
  get drawCalls(): number {
    let n = 0;
    for (const b of this.buckets.values()) if (b.mesh.count > 0) n += 1;
    return n;
  }

  /** Number of distinct materials backing the layer — always 1 (AC). */
  get materialCount(): number {
    return 1;
  }

  /**
   * Grounds still-unresolved rock instances (up to maxSamples raycasts this
   * call) and reports whether more ground-sampling work remains next tick
   * (same "call me again" contract as the other tile-tick-driven layers).
   * Unlike VegetationLayer there is no camera-distance reconcile step — see
   * the file header for why that's unneeded at this instance count.
   */
  update(sampleGround: GroundSampler, maxSamples = Infinity, generation?: number): boolean {
    const gen = generation ?? (this.fallbackGeneration -= 1);
    const finite = maxSamples !== Infinity;
    let samples = 0;
    let exhausted = false;
    for (const rec of this.instances) {
      if (rec.resolvedY !== null) continue; // already grounded
      if (rec.attemptGen === gen) continue; // already tried this generation
      if (finite && samples >= maxSamples) {
        exhausted = true;
        break;
      }
      samples += 1;
      rec.attemptGen = gen;
      const y = sampleGround(rec.x, rec.z);
      if (y === null) continue; // ground not streamed yet — retry next tick
      rec.resolvedY = y;
    }
    // Realise as many grounded rocks as the active cap allows (issue #73). Kept
    // separate from grounding so a live cap change reconciles the realised set
    // without re-raycasting: grounded rocks stay grounded.
    this.reconcileRealisation();
    this.flush();
    return exhausted;
  }

  /** Grows or shrinks the realised (drawn) rock set to the active cap, using
   * already-grounded instances only. Grow claims free/fresh slots; shrink frees
   * the most-recently-claimed. Cheap slot bookkeeping — no raycasts, no
   * geometry work. */
  private reconcileRealisation(): void {
    if (this.liveCount < this.cap) {
      for (const rec of this.instances) {
        if (this.liveCount >= this.cap) break;
        if (rec.resolvedY === null || rec.slot) continue;
        this.claim(rec);
      }
    } else if (this.liveCount > this.cap) {
      for (let i = this.instances.length - 1; i >= 0 && this.liveCount > this.cap; i--) {
        if (this.instances[i].slot) this.free(this.instances[i]);
      }
    }
  }

  private claim(rec: InstanceRec): void {
    if (rec.resolvedY === null || rec.slot || this.liveCount >= this.cap) return;
    const bucket = this.buckets.get(rec.preset)!;
    let index: number;
    if (bucket.freed.length > 0) {
      index = bucket.freed.pop() as number;
    } else if (bucket.nextFresh < this.allocCap) {
      index = bucket.nextFresh++;
      bucket.mesh.count = bucket.nextFresh;
    } else {
      return; // this preset's bucket is full at the allocation cap
    }
    this.liveCount += 1;
    rec.slot = { preset: rec.preset, index };

    // Ground the CONTACT RING (the geometry's lowest vertex), not the
    // instance origin, so the rock sits on the DTM rather than floating
    // (origin above ground) or sinking (origin below it).
    this._pos.set(rec.x, rec.resolvedY - bucket.minY * rec.scale, rec.z);
    this._quat.setFromAxisAngle(Y_AXIS, rec.rotationY);
    this._scl.set(rec.scale, rec.scale, rec.scale);
    this._mat.compose(this._pos, this._quat, this._scl);
    bucket.mesh.setMatrixAt(index, this._mat);
    bucket.dirty = true;
  }

  private free(rec: InstanceRec): void {
    if (!rec.slot) return;
    const bucket = this.buckets.get(rec.slot.preset)!;
    bucket.mesh.setMatrixAt(rec.slot.index, ZERO_SCALE_MATRIX);
    bucket.freed.push(rec.slot.index);
    bucket.dirty = true;
    this.liveCount -= 1;
    rec.slot = null;
  }

  /**
   * Changes the live instance cap (issue #73). Clamped to the allocation
   * capacity. Reconciles the realised set immediately from already-grounded
   * rocks (no re-raycast, no re-placement) and flushes, so the change shows at
   * once. Off the render loop: call once on a user click, never per frame.
   */
  setInstanceCap(cap: number): void {
    const clamped = Math.max(0, Math.min(Math.floor(cap), this.allocCap));
    if (clamped === this.cap) return;
    this.cap = clamped;
    this.reconcileRealisation();
    this.flush();
  }

  /** Active instance cap (issue #73 live quality re-cap). */
  get instanceCap(): number {
    return this.cap;
  }

  private flush(): void {
    for (const b of this.buckets.values()) {
      if (b.dirty) {
        b.mesh.instanceMatrix.needsUpdate = true;
        b.dirty = false;
      }
    }
  }

  private applyRockShader(shader: WebGLProgramParametersWithUniforms): void {
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `#include <common>
attribute vec3 aVdata;
varying vec3 vRockData;
varying vec3 vRockWorld;`,
      )
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
vRockData = aVdata;
vRockWorld = ( modelMatrix * instanceMatrix * vec4( transformed, 1.0 ) ).xyz;`,
      );

    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
varying vec3 vRockData;
varying vec3 vRockWorld;`,
      )
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
{
  vec3 baseCol = ${glslVec3(srgbToLinearRgb(ROCK_BASE_SRGB))};
  vec3 bandCol = ${glslVec3(srgbToLinearRgb(ROCK_BAND_SRGB))};
  vec3 mossCol = ${glslVec3(srgbToLinearRgb(MOSS_SRGB))};
  float strataT = vRockData.x;
  float openness = vRockData.y;
  float cavity = vRockData.z;
  vec3 col = mix( baseCol, bandCol, smoothstep( 0.15, 0.85, strataT ) );
  // Moss/lichen: gated by the per-vertex openness ("upness") proxy and a
  // coarse world-space cell hash for patchiness — the same hash idiom used
  // elsewhere in this codebase's seeded scatter shaders, reused rather than a
  // new noise scheme.
  vec2 cell = floor( vRockWorld.xz / 0.6 );
  // "patchMask", not "patch": patch is a GLSL ES 3.00 reserved word and
  // fails to compile under WebGL2.
  float patchMask = fract( sin( dot( cell, vec2( 12.9898, 78.233 ) ) ) * 43758.5453 );
  float mossW = smoothstep( 0.35, 0.75, openness ) * step( 0.45, patchMask );
  col = mix( col, mossCol, mossW * 0.7 );
  col *= mix( 0.55, 1.0, cavity ); // cavity AO darkens creases
  diffuseColor.rgb = col;
}`,
      );
  }
}
